import React, {useCallback, useContext, useEffect} from "react";
import {observer} from "mobx-react-lite";

import {Context} from "../../../index";
import {useSocket} from "../../../store/SocketContext";

import {
    DIRECT_CHATS_ROUTE,
    REQUEST_CHAT_ROUTE
} from "../../../utils/consts";

const ChatSocketBridge = observer(() => {
    const {
        user,
        apartmentChat,
        directChat,
        requestChat
    } = useContext(Context);

    const socket = useSocket();

    const currentUserId = user?.UserData?.id;
    const currentUserRole = user?.UserData?.role;

    const handleChatListUpdate = useCallback((data) => {

        const sessionFromSocket = data?.session;

        if (!sessionFromSocket?.id) {
            return;
        }

        const isCurrentUserOwner =
            user.isAuth &&
            sessionFromSocket.flowType === "owner" &&
            Number(sessionFromSocket.ownerId) === Number(currentUserId);

        const isCurrentUserManager =
            user.isAuth &&
            sessionFromSocket.flowType === "manager" &&
            (
                Number(sessionFromSocket.managerId) === Number(currentUserId) ||
                currentUserRole === "ADMIN"
            );

        let unreadCount = 0;

        if (isCurrentUserOwner) {
            unreadCount = Number(sessionFromSocket.ownerUnreadCount || 0);
        } else if (isCurrentUserManager) {
            unreadCount = Number(sessionFromSocket.managerUnreadCount || 0);
        } else {
            unreadCount = Number(sessionFromSocket.guestUnreadCount || 0);
        }

        const sessionToUpdate = {
            ...sessionFromSocket,
            unreadCount
        };

        apartmentChat.upsertSession(sessionToUpdate);
    }, [
        apartmentChat,
        user.isAuth,
        currentUserId,
        currentUserRole
    ]);

    const handleDirectChatListUpdate = useCallback((data) => {

        const updatedChat = data?.chat;

        if (!updatedChat?.id) {
            return;
        }

        directChat.upsertSession(updatedChat);

        const isCurrentDirectChatOpen =
            window.location.pathname === `${DIRECT_CHATS_ROUTE}/${updatedChat.id}`;

        const hasUnread = Number(updatedChat.unreadCount || 0) > 0;

        if (!isCurrentDirectChatOpen && hasUnread) {
            directChat.setLastNotification(updatedChat);
        }
    }, [directChat]);

    const handleRequestChatNewMessage = useCallback((message) => {

        if (!message?.id) {
            return;
        }

        const wasAdded = requestChat.addMessage(message);

        const isMine =
            message?.senderUserId &&
            currentUserId &&
            Number(message.senderUserId) === Number(currentUserId);

        const isRequestChatOpen =
            window.location.pathname === REQUEST_CHAT_ROUTE;

        const isAlreadyReadByState =
            requestChat.lastReadMessageId &&
            Number(message.id) <= Number(requestChat.lastReadMessageId);

        if (
            wasAdded &&
            !isMine &&
            !isRequestChatOpen &&
            !isAlreadyReadByState
        ) {
            requestChat.incrementUnread(message);
        }
    }, [
        requestChat,
        currentUserId
    ]);

    useEffect(() => {
        if (!user.isLoaded || !socket) {
            return;
        }

        const guestSessionId =
            localStorage.getItem("sessionId") ||
            localStorage.getItem("guestSessionId");

        let roomName = null;

        if (user.isAuth && currentUserId) {
            roomName = `user-${currentUserId}`;
        } else if (guestSessionId) {
            roomName = `guest-${guestSessionId}`;
        }

        const join = () => {
            if (roomName) {
                socket.emit("joinRoom", {roomName}, (response) => {
                    if (!response?.ok) {
                        console.warn("joinRoom failed:", response?.message);
                        return;
                    }

                });
            }

            if (user.isAuth && currentUserId) {
                socket.emit("joinRequestChat", {}, (response) => {
                    if (!response?.ok) {
                        console.warn("joinRequestChat failed:", response?.message);
                        return;
                    }
                });
            }
        };

        join();

        socket.on("connect", join);

        socket.on("chatListUpdate", handleChatListUpdate);
        socket.on("directChatListUpdate", handleDirectChatListUpdate);
        socket.on("requestChatNewMessage", handleRequestChatNewMessage);

        return () => {
            socket.off("connect", join);

            socket.off("chatListUpdate", handleChatListUpdate);
            socket.off("directChatListUpdate", handleDirectChatListUpdate);
            socket.off("requestChatNewMessage", handleRequestChatNewMessage);
        };
    }, [
        socket,
        user.isLoaded,
        user.isAuth,
        currentUserId,
        handleChatListUpdate,
        handleDirectChatListUpdate,
        handleRequestChatNewMessage
    ]);

    return null;
});

export default ChatSocketBridge;